import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  bodyErrors,
  bodyTargetErrors,
  errorSchema,
  guardErrors,
  iconKeySchema,
  idParams,
  targetErrors,
} from '../common';
import { usefulLinkSchema } from '../entities';
import { labelInput, urlInput } from '../inputs';

const c = initContract();

/** Deployments of the AI Dude service; the API maps each to a base URL. */
export const AI_DUDE_ENVS = ['local', 'dev', 'prod'] as const;
export const aiDudeEnvSchema = z.enum(AI_DUDE_ENVS);
export type AiDudeEnv = z.infer<typeof aiDudeEnvSchema>;

export const AI_DUDE_DEFAULT_LOCAL_PORT = 3000;

/**
 * Only the port of `local` is caller-chosen — the host stays `localhost`, so
 * the browser can never point the API at an arbitrary address.
 */
export const aiDudeQuery = z.object({
  localPort: z.coerce.number().int().min(1).max(65535).optional(),
});
export type AiDudeQuery = z.infer<typeof aiDudeQuery>;

export const aiDudeEnvironmentSchema = z.object({
  env: aiDudeEnvSchema,
  apiUrl: z.string(),
});
export type AiDudeEnvironmentDto = z.infer<typeof aiDudeEnvironmentSchema>;

/**
 * AI Dude counts as available only when `GET /` answers with the expected
 * `{ status, version }` body; a failed connection or any other answer is
 * unavailable. Either way it is a result, not an error: the debugger exists to
 * show it, so it comes back as 200 rather than a 502.
 */
export const aiDudeStatusSchema = z.object({
  env: aiDudeEnvSchema,
  apiUrl: z.string(),
  available: z.boolean(),
  /** HTTP status of the probe; null when no response came back at all. */
  httpStatus: z.number().int().nullable(),
  latencyMs: z.number().int(),
  /** From AI Dude's `GET /` body; null when unavailable. */
  serviceStatus: z.string().nullable(),
  version: z.string().nullable(),
  /** Why it is unavailable; null when available. */
  error: z.string().nullable(),
});
export type AiDudeStatusDto = z.infer<typeof aiDudeStatusSchema>;

export const linkBody = z.object({
  label: labelInput,
  url: urlInput,
  iconKey: iconKeySchema,
});

/** Post-parse: `iconKey` is narrowed to the enum, `url` normalised by `URL`. */
export type UsefulLinkBody = z.infer<typeof linkBody>;

/**
 * Useful-links CRUD plus the AI Dude debugger. The Smalltalk script generators
 * are pure functions with no DB access, so they run in the browser and have no
 * endpoint at all. AI Dude is never called from the browser: every request goes
 * through these routes, which pick the base URL from the env.
 *
 * These rows have no `userId` — the table is shared across everyone with `ag5`
 * access, deliberately, so there is no per-row ownership check to express.
 */
export const ag5Contract = c.router(
  {
    listLinks: {
      method: 'GET',
      path: '/links',
      responses: { 200: z.array(usefulLinkSchema), ...guardErrors },
    },

    createLink: {
      method: 'POST',
      path: '/links',
      body: linkBody,
      responses: { 201: usefulLinkSchema, ...bodyErrors },
    },

    updateLink: {
      method: 'PATCH',
      path: '/links/:id',
      pathParams: idParams,
      body: linkBody,
      responses: { 200: usefulLinkSchema, ...bodyTargetErrors },
    },

    deleteLink: {
      method: 'DELETE',
      path: '/links/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },

    listAiDudeEnvironments: {
      method: 'GET',
      path: '/ai-dude/environments',
      responses: { 200: z.array(aiDudeEnvironmentSchema), ...guardErrors },
    },

    getAiDudeStatus: {
      method: 'GET',
      path: '/ai-dude/:env/status',
      pathParams: z.object({ env: aiDudeEnvSchema }),
      query: aiDudeQuery,
      responses: { 200: aiDudeStatusSchema, 400: errorSchema, ...guardErrors },
    },
  },
  { pathPrefix: '/ag5' },
);
